import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import { KeyIcon } from '@heroicons/react/24/outline';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Button } from '../components/Buttons/Button.js';
import { useOptionalIdp } from '../providers/IdpProvider.js';
import { toast } from '../utils/toast.js';
import { getPasskeyHttpStatus, registerPasskeyWithIdp, } from './passkeyRegistration.js';
export function mapTeeTimePasskeyRegistrationError(error) {
    const status = getPasskeyHttpStatus(error);
    if (status === 503) {
        return 'Passkeys are not available right now. Use WhatsApp, SMS, or email to finish MFA setup.';
    }
    if (status === 401) {
        return 'Sign in again before adding a passkey.';
    }
    if (status === 403) {
        return 'Your security session expired. Refresh and try again.';
    }
    if (error instanceof Error && error.name === 'NotAllowedError') {
        return 'Passkey request was cancelled.';
    }
    if (error instanceof Error && error.name === 'NotSupportedError') {
        return 'Passkeys are not supported on this device.';
    }
    if (error instanceof Error && error.message) {
        return error.message;
    }
    return 'Unable to add passkey.';
}
export function mapTeeTimePasskeyAvailabilityError(error) {
    const status = getPasskeyHttpStatus(error);
    if (status === 503) {
        return 'Passkeys are not available right now. Use WhatsApp, SMS, or email to finish MFA setup.';
    }
    if (status === 401) {
        return 'Sign in again before adding a passkey.';
    }
    if (status === 403) {
        return 'Refresh the page to renew your security session.';
    }
    return 'Passkeys are temporarily unavailable.';
}
function isAvailabilityBlockingError(error) {
    const status = getPasskeyHttpStatus(error);
    return status === 503 || status === 401 || status === 403;
}
export const PasskeyRegistrationBlock = ({ className, compact = false, idpClient, testId = 'passkey-registration-block', }) => {
    const providerIdp = useOptionalIdp();
    const api = idpClient ?? providerIdp;
    const supported = useMemo(() => typeof window !== 'undefined' && browserSupportsWebAuthn(), []);
    const [registering, setRegistering] = useState(false);
    const [registered, setRegistered] = useState(false);
    const [availability, setAvailability] = useState(api && supported ? 'checking' : 'unavailable');
    const [error, setError] = useState(null);
    useEffect(() => {
        let active = true;
        if (!api || !supported) {
            setAvailability('unavailable');
            return () => {
                active = false;
            };
        }
        setAvailability('checking');
        setError(null);
        void api
            .listPasskeys()
            .then(() => {
            if (active) {
                setAvailability('available');
            }
        })
            .catch((availabilityError) => {
            if (active) {
                setAvailability('unavailable');
                setError(mapTeeTimePasskeyAvailabilityError(availabilityError));
            }
        });
        return () => {
            active = false;
        };
    }, [api, supported]);
    const disabled = registering || registered || availability !== 'available';
    const badgeText = registered
        ? 'Added'
        : availability === 'checking'
            ? 'Checking'
            : availability === 'available'
                ? 'Available'
                : 'Unavailable';
    const helperText = registered
        ? 'Passkey added. Finish the code step to complete MFA setup.'
        : availability === 'checking'
            ? 'Checking whether passkeys are enabled for this session.'
            : availability === 'available'
                ? 'Add a passkey for faster sign-in on this device.'
                : 'Use WhatsApp, SMS, or email to finish MFA setup today.';
    const handleRegister = async () => {
        if (!api || availability !== 'available' || registered) {
            return;
        }
        setError(null);
        setRegistering(true);
        try {
            await registerPasskeyWithIdp(api);
            setRegistered(true);
            toast.success('Passkey added.');
        }
        catch (registrationError) {
            const message = mapTeeTimePasskeyRegistrationError(registrationError);
            if (isAvailabilityBlockingError(registrationError)) {
                setAvailability('unavailable');
            }
            setError(message);
            toast.error(message);
        }
        finally {
            setRegistering(false);
        }
    };
    return (_jsx("div", { className: [
            'w-full rounded-md border border-gray-200 bg-white text-left shadow-sm',
            compact ? 'px-4 py-3 text-sm' : 'p-4',
            className,
        ]
            .filter(Boolean)
            .join(' '), "data-testid": testId, children: _jsxs("div", { className: "flex items-start gap-3", children: [_jsx("span", { className: [
                        'inline-flex shrink-0 items-center justify-center rounded-full bg-slate-900 text-white',
                        compact ? 'h-8 w-8' : 'h-9 w-9',
                    ].join(' '), "aria-hidden": "true", children: _jsx(KeyIcon, { className: compact ? 'h-4 w-4' : 'h-5 w-5', "aria-hidden": "true" }) }), _jsxs("div", { className: "min-w-0 flex-1 space-y-2", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx("p", { className: "font-semibold text-gray-900", children: "Passkeys" }), _jsx("span", { className: [
                                        'rounded-full px-2 py-0.5 font-medium uppercase tracking-wide',
                                        compact ? 'text-[10px]' : 'text-[11px]',
                                        registered
                                            ? 'bg-green-100 text-green-700'
                                            : availability === 'available'
                                                ? 'bg-blue-50 text-blue-700'
                                                : availability === 'checking'
                                                    ? 'bg-gray-100 text-gray-600'
                                                    : 'bg-gray-100 text-gray-600',
                                    ].join(' '), children: badgeText })] }), _jsx("p", { className: compact ? 'text-xs text-gray-600' : 'text-sm text-gray-600', children: helperText }), _jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [_jsx(Button, { htmlType: "button", variant: "secondary", size: "small", loading: registering, disabled: disabled, onClick: handleRegister, children: registered ? 'Passkey added' : registering ? 'Adding passkey' : 'Add passkey' }), error ? (_jsx("p", { className: "text-xs text-red-600", role: "alert", children: error })) : null] })] })] }) }));
};
//# sourceMappingURL=PasskeyRegistrationBlock.js.map